import { Column } from "components/table/Table";
import map from "lodash/map";
import round from "lodash/round";
import startsWith from "lodash/startsWith";
import React from "react";
import { GetScore, Meet } from "types";
import { getDotsAndAgePoints, getDotsPoints } from "util/formulas/dots";
import {
  getGlossbrennerAndAgePoints,
  getGlossbrennerPoints,
} from "util/formulas/glossbrenner";
import { getIpfAndAgePoints, getIpfPoints } from "util/formulas/ipf";
import { getKPoints } from "util/formulas/kPoints";
import { getParaDotsPoints } from "util/formulas/paraDots";
import { getPercentOfRecordPoints } from "util/formulas/percentOfRecord";
import { getReshelAndAgePoints, getReshelPoints } from "util/formulas/reshel";
import {
  getSchwartzMaloneAndAgePoints,
  getSchwartzMalonePoints,
} from "util/formulas/schwartzMalone";
import { getWilksAndAgePoints, getWilksPoints } from "util/formulas/wilks";
import {
  getWilks2020AndAgePoints,
  getWilks2020Points,
} from "util/formulas/wilks2020";
import { getZscoreAndAgePoints, getZscorePoints } from "util/formulas/zscore";
import { getDivisionDoc, getTotal } from "util/lifterHelper";

const scoreFunctions: Record<string, GetScore> = {
  total: getTotal,
  forecastedTotal: getTotal,
  wilks: getWilksPoints,
  forecastedWilks: getWilksPoints,
  wilksAge: getWilksAndAgePoints,
  forecastedWilksAge: getWilksAndAgePoints,
  wilks2020: getWilks2020Points,
  forecastedWilks2020: getWilks2020Points,
  wilks2020Age: getWilks2020AndAgePoints,
  forecastedWilks2020Age: getWilks2020AndAgePoints,
  ipfPoints: getIpfPoints,
  forecastedIpfPoints: getIpfPoints,
  ipfAgePoints: getIpfAndAgePoints,
  forecastedIpfAgePoints: getIpfAndAgePoints,
  dotsPoints: getDotsPoints,
  forecastedDotsPoints: getDotsPoints,
  dotsAgePoints: getDotsAndAgePoints,
  forecastedDotsAgePoints: getDotsAndAgePoints,
  schwartzMalonePoints: getSchwartzMalonePoints,
  schwartzMaloneAndAgePoints: getSchwartzMaloneAndAgePoints,
  glossbrennerPoints: getGlossbrennerPoints,
  glossbrennerAndAgePoints: getGlossbrennerAndAgePoints,
  reshelPoints: getReshelPoints,
  reshelPointsAndAgePoints: getReshelAndAgePoints,
  paraDotsPoints: getParaDotsPoints,
  forecastedParaDotsPoints: getParaDotsPoints,
  percentOfRecord: getPercentOfRecordPoints,
  forecastedPercentOfRecord: getPercentOfRecordPoints,
  kPoints: getKPoints,
  zScore: getZscorePoints,
  forecastedZscore: getZscorePoints,
  zScoreAgePoints: getZscoreAndAgePoints,
  forecastedZscoreAgePoints: getZscoreAndAgePoints,
};

const ScoreCell = ({
  data,
  column,
  meet,
  style,
}: {
  data: any;
  column: Column;
  meet: Meet;
  style: React.CSSProperties & { fontSize: number };
}) => {
  const lifter = data.lifter;
  if (data.row === "header") {
    return (
      <div style={style} className="table-cell-inner">
        {column.label}
      </div>
    );
  } else if (data.row === "title") {
    return <div style={style} className="table-cell-inner" />;
  } else {
    if (lifter.divisions.length < 2) {
      style = { ...style, fontSize: style.fontSize * 1.3 };
    }
    return (
      <div style={style} className="table-cell-inner">
        {map(lifter.divisions, (lifterDivision, index: number) => {
          let rowStyle: React.CSSProperties = {
            paddingBottom: style.padding,
          };

          if (!lifterDivision.divisionId) {
            return null;
          }

          const division = getDivisionDoc(lifterDivision.divisionId, meet);
          if (!division) {
            return null;
          }

          if (division.hideOnBoard) {
            return null;
          }

          if (index !== 0) {
            rowStyle = {
              ...rowStyle,
              paddingTop: style.padding,
            };
          }

          const scoreFunc =
            scoreFunctions[column.key as keyof typeof scoreFunctions];

          return (
            <div
              key={`${lifterDivision.divisionId}-${index}`}
              style={rowStyle}
              className="cell-row"
            >
              {round(
                scoreFunc({
                  lifter,
                  division,
                  meet,
                  forecasted: startsWith(column.key, "forecasted"),
                }),
                3
              )}
            </div>
          );
        })}
      </div>
    );
  }
};

export default ScoreCell;
